import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';

const riskColor = (r: string) => r === 'critical' ? 'bg-red-100 text-red-700 border-red-200' : r === 'high' ? 'bg-orange-100 text-orange-700 border-orange-200' : r === 'moderate' || r === 'medium' ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-green-100 text-green-700 border-green-200';

export default function SupOversightPage() {
  const [cases, setCases] = useState<any[]>([]);
  const [selectedCase, setSelectedCase] =
  useState<any>(null);
  const navigate = useNavigate();

  const getRisk = (c: any) => {
    let score = 75;

    switch (c.incident_type) {
      case 'Sexual Abuse':
      case 'Child Trafficking':
      case 'Online Sexual Exploitation':
        score = 95;
        break;
      case 'Physical Abuse':
        score = 90;
        break;
      case 'Emotional Abuse':
        score = 80;
        break;
      case 'Neglect':
        score = 75;
        break;
      default:
        score = 70;
    }

    if (c.prior_incidents === 'yes') score += 5;
    if (c.priority_level === 'urgent') score += 3;
    if (c.priority_level === 'emergency') score += 5;

    if (score > 100) score = 100;

    let level = 'low';
    if (score >= 90) level = 'critical';
    else if (score >= 80) level = 'high';
    else if (score >= 75) level = 'moderate';

    return { score, level };
  };

  useEffect(() => {
    const fetchCases = async () => {
      try {
        const response = await fetch(
          "https://guardiancloudcare.com/backend/api/get_supervisor_cases.php"
        );
        const data = await response.json();
        console.log(data);
        if (Array.isArray(data)) {
          setCases(data);
        } else {
          setCases([]);
        }
      } catch (error) {
        console.error(error);
        setCases([]);
      }
    };
    fetchCases();
  }, []);

  const mainCases =
    cases.filter(
      (c) => c.record_type === 'main_case'
    );

  const submittedCases =
    cases.filter(
      (c) => c.record_type === 'submitted_case'
    );

  return (
    <AppLayout title="Case Oversight" breadcrumb={['Supervisor', 'Oversight']}>
      {selectedCase && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-5">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b">
              <div>
                <h2 className="text-xl font-bold text-gray-800">
                  Case Full Details
                </h2>
                <p className="text-sm text-gray-400">
                  {selectedCase.case_number}
                </p>
              </div>
              <button
                onClick={() => setSelectedCase(null)}
                className="text-2xl text-gray-400 hover:text-red-500"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-4 text-sm">
              {Object.entries(selectedCase).map(([key, value]) => (
                <div
                  key={key}
                  className="border rounded-xl p-3"
                >
                  <p className="text-xs text-gray-400 mb-1 uppercase">
                    {key.replaceAll('_', ' ')}
                  </p>
                  <p className="font-medium text-gray-800 break-words">
                    {String(value || 'N/A')}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">All Cases — Read-Only Oversight</h3>
          <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-3 py-1.5 rounded-lg flex items-center gap-1"><i className="ri-eye-line" />View Only Mode</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                <th className="px-5 py-3 text-left font-medium">Case</th>
                <th className="px-5 py-3 text-left font-medium">Child</th>
                <th className="px-5 py-3 text-left font-medium hidden md:table-cell">Incident</th>
                <th className="px-5 py-3 text-left font-medium">Risk</th>
                <th className="px-5 py-3 text-left font-medium">Status</th>
                <th className="px-5 py-3 text-left font-medium hidden lg:table-cell">Worker</th>
                <th className="px-5 py-3 text-left font-medium">Approval</th>
                <th className="px-5 py-3 text-left font-medium">View</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {mainCases.map((c, index) => {
                const risk = getRisk(c);
                return (
                  <tr
                    key={`main-${c.case_number}-${index}`}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-5 py-3 text-xs font-mono text-gray-500">
                      {c.case_number?.split('-').slice(-1)[0]}
                    </td>
                    <td className="px-5 py-3">
                      <div>
                        <p className="font-medium text-gray-800">
                          {c.first_name} {c.last_name}
                        </p>
                        <p className="text-xs text-gray-400">
                          {c.age} yrs · {c.gender}
                        </p>
                      </div>
                    </td>
                    <td className="px-5 py-3 hidden md:table-cell text-xs text-gray-600">
                      {c.incident_type}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`text-xs px-2 py-1 rounded-full border font-medium capitalize ${riskColor(risk.level)}`}
                      >
                        {risk.level} ({risk.score})
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-xs px-2 py-1 rounded-full bg-teal-100 text-teal-700">
                        {c.case_status || 'Ongoing'}
                      </span>
                    </td>
                    <td className="px-5 py-3 hidden lg:table-cell text-xs text-gray-600">
                      {c.worker_name || 'Unknown Worker'}
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">
                        Active
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <button
                        onClick={() => setSelectedCase(c)}
                        className="text-xs text-teal-500 hover:text-teal-700"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody> 
          </table>
          <div className="mt-8 bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">
                Submitted Cases
              </h3>
              <span className="text-xs bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1.5 rounded-lg">
                {submittedCases.length} Submitted
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                    <th className="px-5 py-3 text-left font-medium">
                      Case
                    </th>
                    <th className="px-5 py-3 text-left font-medium">
                      Child
                    </th>
                    <th className="px-5 py-3 text-left font-medium">
                      Incident
                    </th>
                    <th className="px-5 py-3 text-left font-medium">
                      Status
                    </th>
                    <th className="px-5 py-3 text-left font-medium">
                      Worker
                    </th>
                    <th className="px-5 py-3 text-left font-medium">
                      Submitted
                    </th>
                    <th className="px-5 py-3 text-left font-medium">
                      View
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {submittedCases.map((c, index) => {
                    const risk = getRisk(c);
                    return (
                      <tr
                        key={`submitted-${c.case_number}-${index}`}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-5 py-3 text-xs font-mono text-gray-500">
                          {c.case_number?.split('-').slice(-1)[0]}
                        </td>
                        <td className="px-5 py-3">
                          <div>
                            <p className="font-medium text-gray-800">
                              {c.first_name} {c.last_name}
                            </p>
                            <p className="text-xs text-gray-400">
                              {c.age} yrs · {c.gender}
                            </p>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-xs text-gray-600">
                          {c.incident_type}
                        </td>
                        <td className="px-5 py-3">
                          <span
                            className={`text-xs px-2 py-1 rounded-full border font-medium capitalize ${riskColor(risk.level)}`}
                          >
                            {c.status || 'Pending'}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-xs text-gray-600">
                          {c.worker_name || 'Unknown Worker'}
                        </td>
                        <td className="px-5 py-3 text-xs text-gray-500">
                          {c.submitted_at || 'N/A'}
                        </td>
                        <td className="px-5 py-3">
                          <button
                            onClick={() => setSelectedCase(c)}
                            className="text-xs text-blue-500 hover:text-blue-700"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
